import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useBloodTestPackage, useUpdateBloodTestPackage, useDeleteBloodTestPackage, useBiometrics } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function PackageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pkg, isLoading } = useBloodTestPackage(id!);
  const { data: biometrics } = useBiometrics();
  const updatePackage = useUpdateBloodTestPackage();
  const deletePackage = useDeleteBloodTestPackage();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceInNok, setPriceInNok] = useState("0");

  useEffect(() => {
    if (pkg) {
      setName(pkg.name);
      setDescription(pkg.description ?? "");
      setPriceInNok(pkg.priceInNok ?? "0");
    }
  }, [pkg]);

  const handleSave = async () => {
    if (!id) return;
    await updatePackage.mutateAsync({ id, data: { name, description, priceInNok } });
    toast.success("Pakke lagret");
  };

  const handleDelete = async () => {
    if (!id) return;
    await deletePackage.mutateAsync(id);
    toast.success("Pakke slettet");
    navigate("/packages");
  };

  // Parse biometricIds from the package if available
  const packageBiometricIds: string[] = (() => {
    try {
      const raw = (pkg as Record<string, unknown>)?.biometricIds;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  })();

  const packageBiometrics = biometrics?.filter(
    (b) => packageBiometricIds.includes(b.id)
  );

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  if (!pkg) {
    return (
      <div className="text-center py-12">
        <p className="text-admin-text-subtle mb-4">Pakke ikke funnet</p>
        <Button variant="outline" onClick={() => navigate("/packages")}>
          Tilbake til listen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/packages")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">{pkg.name}</h2>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-admin-text-subtle mb-1">Navn</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-medium text-admin-text-subtle mb-1">Beskrivelse</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[160px]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-admin-text-subtle mb-1">Pris (NOK)</label>
            <Input
              type="number"
              value={priceInNok}
              onChange={(e) => setPriceInNok(e.target.value)}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {packageBiometrics && packageBiometrics.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-medium text-admin-text-subtle mb-3">Biomarkorer i denne pakken</h3>
            <div className="flex flex-wrap gap-2">
              {packageBiometrics.map((b) => (
                <Badge key={b.id} variant="secondary">{b.name}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={updatePackage.isPending}>
          <Save className="h-4 w-4" />
          Lagre
        </Button>
        <Button variant="destructive" onClick={handleDelete} disabled={deletePackage.isPending}>
          <Trash2 className="h-4 w-4" />
          Slett
        </Button>
      </div>
    </div>
  );
}
